<script setup lang="ts">
import { computed } from 'vue';
import { Dialog } from '@ark-ui/vue';
import { UiButton, UiIcon, UiText } from '@shopexperts/shared';
import type {
  ReviewImportMeta,
  ReviewImportPreviewRow,
  ReviewImportStatus,
} from '@shopexperts/legacy-api';
import ImportReviewPreviewCard from './ImportReviewPreviewCard.vue';

const props = defineProps<{
  expertName: string;
  rows: ReviewImportPreviewRow[];
  meta: ReviewImportMeta | null;
  fileName?: string | null;
  importing?: boolean;
  errorMessage?: string | null;
  status: ReviewImportStatus;
}>();

const emit = defineEmits<{
  back: [];
  confirm: [];
  close: [];
  'update:status': [value: ReviewImportStatus];
}>();

const open = defineModel<boolean>({ default: false });

function onOpenChange(details: any) {
  const next = typeof details === 'object' && details ? !!details.open : !!details;
  open.value = next;
  if (!next) emit('close');
}

const rowCount = computed(() => props.rows.length);

const summaryChips = computed(() => {
  const summary = props.meta?.summary;
  if (!summary) return [] as Array<{ label: string; value: number; tone: string; icon: string }>;
  return [
    {
      label: 'Matched clients',
      value: summary.matched_existing_client_count,
      tone: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
      icon: 'check-circle',
    },
    {
      label: 'New shadow clients',
      value: summary.shadow_accounts_to_create_count,
      tone: 'bg-amber-50 text-amber-700 ring-amber-100',
      icon: 'user-circle',
    },
    {
      label: 'Ghost fallback',
      value: summary.ghost_fallback_count,
      tone: 'bg-gray-100 text-gray-600 ring-gray-200',
      icon: 'circle',
    },
  ];
});

type StatusOption = {
  value: ReviewImportStatus;
  label: string;
  helper: string;
};

const statusOptions: StatusOption[] = [
  {
    value: 'pending',
    label: 'Pending review',
    helper: 'Reviews land in the moderation queue and stay hidden until an admin approves them.',
  },
  {
    value: 'published',
    label: 'Publish immediately',
    helper: 'Reviews go live the moment you import. Use this only when the source has already been vetted.',
  },
];

const activeStatus = computed(() => statusOptions.find((s) => s.value === props.status) ?? statusOptions[0]!);

const isPublish = computed(() => props.status === 'published');

const importButtonLabel = computed(() => {
  if (props.importing) {
    return isPublish.value ? 'Publishing…' : 'Importing…';
  }
  const noun = `review${rowCount.value === 1 ? '' : 's'}`;
  return isPublish.value
    ? `Publish ${rowCount.value} ${noun} now`
    : `Import ${rowCount.value} ${noun}`;
});

function selectStatus(value: ReviewImportStatus) {
  if (props.importing || value === props.status) return;
  emit('update:status', value);
}
</script>

<template>
  <Teleport to="body" defer>
    <Dialog.Root
      v-model:open="open"
      @openChange="onOpenChange"
      :close-on-escape="!importing"
      :close-on-click-outside="!importing"
      lazyMount
    >
      <Dialog.Backdrop class="se-dialog-overlay fixed inset-0 bg-slate-950/50 backdrop-blur-sm z-100" />
      <Dialog.Positioner class="fixed inset-0 z-100 flex items-center justify-center p-4 sm:p-6">
        <Dialog.Content
          class="pointer-events-auto bg-white rounded-3xl w-full max-w-5xl flex flex-col overflow-hidden shadow-2xl border border-slate-200/60"
          :style="{ maxHeight: 'min(92dvh, 1000px)' }"
        >
          <!-- Sticky header -->
          <header class="px-8 pt-7 pb-5 border-b border-slate-100 bg-linear-to-b from-white via-white to-slate-50/40">
            <div class="flex items-start justify-between gap-6">
              <div class="min-w-0">
                <div class="flex items-center gap-2 mb-2">
                  <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold uppercase tracking-[0.12em]">
                    <span class="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                    Step 2 of 2 · Preview
                  </span>
                </div>
                <Dialog.Title>
                  <UiText
                    as="h3"
                    variant="h4"
                    class="font-semibold text-slate-900 leading-tight"
                  >
                    Preview Import — {{ rowCount }} review{{ rowCount === 1 ? '' : 's' }}
                  </UiText>
                </Dialog.Title>
                <Dialog.Description>
                  <p class="text-sm text-slate-500 mt-1.5">
                    Review the parsed rows for
                    <span class="font-medium text-slate-700">{{ expertName }}</span>
                    <template v-if="fileName">
                      from
                      <span class="font-mono text-[12px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{{ fileName }}</span>
                    </template>
                    before persisting.
                  </p>
                </Dialog.Description>
              </div>

              <Dialog.CloseTrigger as-child>
                <button
                  class="shrink-0 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 inline-flex items-center justify-center transition-colors"
                  :disabled="importing"
                >
                  <UiIcon name="close" class="w-5 h-5" />
                </button>
              </Dialog.CloseTrigger>
            </div>

            <!-- Summary chips -->
            <div v-if="summaryChips.length" class="mt-5 flex flex-wrap gap-2.5">
              <span
                v-for="chip in summaryChips"
                :key="chip.label"
                class="inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full ring-1 text-xs font-medium"
                :class="chip.tone"
              >
                <span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/70">
                  <UiIcon :name="(chip.icon as any)" class="w-3.5 h-3.5" />
                </span>
                <span class="font-semibold tabular-nums">{{ chip.value }}</span>
                <span class="font-normal opacity-80">{{ chip.label }}</span>
              </span>
              <span class="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-500">
                <UiIcon name="file-placeholder" class="w-3.5 h-3.5" />
                <span class="tabular-nums">{{ meta?.row_count ?? rowCount }} rows parsed</span>
              </span>
            </div>
          </header>

          <!-- Error banner -->
          <div
            v-if="errorMessage"
            class="mx-8 mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-3"
          >
            <UiIcon name="close" class="w-4 h-4 text-rose-500 mt-0.5 shrink-0" />
            <div class="flex-1 min-w-0">
              <UiText as="p" variant="p-small" class="font-semibold text-rose-700">
                Import failed
              </UiText>
              <UiText as="p" variant="p-small" class="text-rose-600 wrap-break-word">
                {{ errorMessage }}
              </UiText>
            </div>
          </div>

          <!-- Scrollable body -->
          <div class="flex-1 overflow-y-auto px-8 py-6 bg-slate-50/50">
            <template v-if="rowCount === 0">
              <div class="text-center py-16">
                <div class="inline-flex w-14 h-14 rounded-2xl bg-white border border-slate-200 items-center justify-center text-slate-300 mb-3">
                  <UiIcon name="file-placeholder" class="w-7 h-7" />
                </div>
                <UiText as="p" variant="h6" class="text-slate-500">
                  No rows were returned from the preview.
                </UiText>
              </div>
            </template>
            <template v-else>
              <ul class="flex flex-col gap-4">
                <li
                  v-for="(row, idx) in rows"
                  :key="`${row.client.email}-${idx}`"
                  class="animate-fade-in-up"
                  :style="{ animationDelay: `${Math.min(idx, 12) * 25}ms` }"
                >
                  <div class="flex items-start gap-3">
                    <span
                      class="mt-2 shrink-0 w-7 h-7 rounded-full bg-white border border-slate-200 text-[11px] font-semibold text-slate-500 inline-flex items-center justify-center tabular-nums"
                    >
                      {{ idx + 1 }}
                    </span>
                    <div class="flex-1 min-w-0">
                      <ImportReviewPreviewCard :row="row" />
                    </div>
                  </div>
                </li>
              </ul>
            </template>
          </div>

          <!-- Sticky footer -->
          <footer class="px-8 py-5 border-t border-slate-100 bg-white">
            <div class="flex flex-col lg:flex-row lg:items-end gap-5 lg:gap-8">
              <!-- Status segmented control -->
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
                    Import as
                  </span>
                  <span class="h-px flex-1 bg-slate-100" />
                </div>
                <div
                  role="radiogroup"
                  aria-label="Import status"
                  class="relative inline-flex items-stretch p-1 rounded-xl bg-slate-100/80 ring-1 ring-slate-200/70"
                >
                  <button
                    v-for="option in statusOptions"
                    :key="option.value"
                    type="button"
                    role="radio"
                    :aria-checked="status === option.value"
                    :disabled="importing"
                    class="status-segment group relative inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 select-none disabled:cursor-not-allowed"
                    :class="[
                      status === option.value
                        ? option.value === 'published'
                          ? 'bg-white text-slate-900 shadow-[0_1px_0_rgba(15,23,42,0.04),0_8px_18px_-12px_rgba(15,23,42,0.25)] ring-1 ring-amber-300/60'
                          : 'bg-white text-slate-900 shadow-[0_1px_0_rgba(15,23,42,0.04),0_8px_18px_-12px_rgba(15,23,42,0.25)] ring-1 ring-slate-300/70'
                        : 'text-slate-500 hover:text-slate-800',
                    ]"
                    @click="selectStatus(option.value)"
                  >
                    <span
                      class="relative inline-flex w-2 h-2"
                      aria-hidden="true"
                    >
                      <span
                        v-if="option.value === 'published' && status === option.value"
                        class="absolute inset-0 rounded-full bg-amber-400 opacity-60 animate-ping"
                      />
                      <span
                        class="relative w-2 h-2 rounded-full"
                        :class="[
                          option.value === 'published'
                            ? status === option.value ? 'bg-amber-500' : 'bg-slate-300'
                            : status === option.value ? 'bg-slate-700' : 'bg-slate-300',
                        ]"
                      />
                    </span>
                    <span class="whitespace-nowrap">{{ option.label }}</span>
                  </button>
                </div>
                <div class="mt-2 flex items-start gap-2 text-[12px] leading-5 text-slate-500 max-w-xl">
                  <span
                    class="mt-1 inline-flex w-1 h-1 rounded-full shrink-0"
                    :class="isPublish ? 'bg-amber-400' : 'bg-slate-400'"
                  />
                  <span>{{ activeStatus.helper }}</span>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex gap-2.5 lg:shrink-0 self-stretch lg:self-end">
                <UiButton
                  variant="outline"
                  label="Change file"
                  :disabled="importing"
                  @click="emit('back')"
                />
                <button
                  type="button"
                  class="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-white text-sm font-semibold tracking-wide disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow"
                  :class="isPublish
                    ? 'bg-amber-600 hover:bg-amber-700 ring-1 ring-amber-700/30'
                    : 'bg-emerald-600 hover:bg-emerald-700'"
                  :disabled="importing || rowCount === 0"
                  @click="emit('confirm')"
                >
                  <span v-if="importing" class="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  <UiIcon v-else :name="isPublish ? 'arrow-send' : 'check'" class="w-4 h-4" />
                  <span>{{ importButtonLabel }}</span>
                </button>
              </div>
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  </Teleport>
</template>

<style scoped>
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out both;
}
</style>
